/**
 * PastasPage — rota /pastas e /pastas/* (RF-M7-006, US-08 AC6).
 * Navega pela árvore do pendrive por breadcrumb; consome GET /pastas e
 * GET /pastas/{caminho}. Preserva o caminho literal (acentos/espaços/maiúsculas).
 * ADR-048: a evidência complementar com o tipo em palavras e, na coluna "Visita", "ver visita ligada" (ou "sem visita
 * ligada").
 */

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePastas } from '../hooks/useConsulta';
import { MarcadorPill } from '../components/ConsultaShared';
import { ehEvidenciaComplementar, rotuloTipoDocumento } from '../components/format';

function decodificar(parte: string): string {
  try {
    return decodeURIComponent(parte);
  } catch {
    // '%' solto (URL digitada à mão): fica como veio, em vez de quebrar a tela.
    return parte;
  }
}

/**
 * Caminho da pasta a partir da URL: o que vem depois de `/pastas/`, parte a parte decodificado.
 * Não usa `params['*']`: o App monta as telas num `<Routes>` sob `<Route path="/*">`, e na rota
 * exata `/pastas` o `*` herdado da rota-pai vale "pastas" — a raiz pedia a pasta "pastas".
 */
export function caminhoDaUrl(pathname: string): string {
  return pathname
    .replace(/^\/pastas(?=\/|$)/, '')
    .split('/')
    .filter((p) => p.length > 0)
    .map(decodificar)
    .join('/');
}

/** A rota de uma pasta: `/pastas/<parte>/<parte>`, cada parte codificada (acentos, espaços, `#`). */
function urlDaPasta(caminho: string): string {
  return `/pastas/${caminho.split('/').filter(Boolean).map(encodeURIComponent).join('/')}`;
}

function Breadcrumb({ caminho }: { caminho: string }): JSX.Element {
  const navigate = useNavigate();
  const partes = caminho.split('/').filter((p) => p.length > 0);
  return (
    <div className="breadcrumb">
      <button onClick={() => navigate('/pastas')}>Raiz</button>
      {partes.map((parte, i) => {
        const ate = partes.slice(0, i + 1).join('/');
        return (
          <span key={ate}>
            <span className="sep">/</span>
            <button onClick={() => navigate(`/pastas/${ate.split('/').map(encodeURIComponent).join('/')}`)}>
              {parte}
            </button>
          </span>
        );
      })}
    </div>
  );
}

export function PastasPage(): JSX.Element {
  const caminho = caminhoDaUrl(useLocation().pathname);
  const navigate = useNavigate();
  const { arvore, carregando, erro } = usePastas(caminho);

  return (
    <div>
      <h1 className="page-title">Pastas</h1>
      <p className="page-subtitle">Navegue pela estrutura original do pendrive.</p>

      <Breadcrumb caminho={caminho} />

      {carregando && <p className="muted">Carregando…</p>}
      {erro && <p className="notice">Não foi possível carregar a pasta.</p>}

      {arvore && (
        <>
          <section className="card">
            <h2>Subpastas</h2>
            {arvore.pastas.length === 0 ? (
              <p className="muted">Nenhuma subpasta.</p>
            ) : (
              <ul className="folder-list">
                {arvore.pastas.map((p) => (
                  <li key={p.caminho}>
                    <button
                      className="link-btn"
                      onClick={() => navigate(urlDaPasta(p.caminho))}
                    >
                      📁 {p.nome}
                    </button>
                    <span className="muted">{p.qtdArquivos} arquivo(s)</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card">
            <h2>Arquivos</h2>
            {arvore.arquivos.length === 0 ? (
              <p className="muted">Nenhum arquivo diretamente nesta pasta.</p>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Arquivo</th>
                    <th>Tipo</th>
                    <th>Marcador</th>
                    <th>Visita</th>
                  </tr>
                </thead>
                <tbody>
                  {arvore.arquivos.map((a) => (
                    <tr key={a.caminhoOriginal}>
                      <td className="mono">
                        {a.nome}
                        {(a.documentos ?? 1) > 1 && <span className="muted"> · {a.documentos} páginas</span>}
                        {a.tipoDocumento === 'COMPACTADO' && (a.extraidos ?? 0) > 0 && (
                          // ADR-043: os arquivos de dentro ficam na pasta de mesmo nome do compactado.
                          <>
                            {' '}
                            <button className="link-btn" onClick={() => navigate(urlDaPasta(a.caminhoOriginal))}>
                              abrir a pasta
                            </button>
                          </>
                        )}
                      </td>
                      <td>{rotuloTipoDocumento(a.tipoDocumento, a.extraidos)}</td>
                      <td>
                        <MarcadorPill marcador={a.marcador} />
                      </td>
                      <td>
                        {(a.visitas ?? 0) > 1 ? (
                          // ADR-033: um PDF gera uma visita por página — a lista do arquivo.
                          <Link to={`/visitas?pasta=${encodeURIComponent(a.caminhoOriginal)}`}>{a.visitas} visitas</Link>
                        ) : a.temVisita && a.visitaId ? (
                          <Link to={`/visitas/${a.visitaId}`}>ver visita</Link>
                        ) : a.visitaLigadaId ? (
                          // ADR-048: a evidência complementar ligada a uma visita (a ligação é calculada).
                          <Link to={`/visitas/${a.visitaLigadaId}`}>ver visita ligada</Link>
                        ) : a.visitaLigadaId === null && ehEvidenciaComplementar(a.tipoDocumento) ? (
                          <span className="muted">sem visita ligada</span>
                        ) : a.naoGeraVisita ? (
                          // Requisito 6.2: lido, e nenhum documento é termo (RN-M2-006).
                          <span className="muted">não gera visita</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
